import { Heading } from "@/components/base/heading";
import { InputErrorMessage } from "@/components/base/input/input-error-message";
import { HorizontalSpacer } from "@/components/layout/horizontal-spacer";
import type { ErrorField } from "@/generated/client.generated";
import { UserCreationErrorCode, userCreationErrorsToString } from "@/utils/error-code-lookup";
import dayjs from "dayjs";
import type Joi from "joi";
import { ErrorMessage, ErrorMessageFlavour } from "../../ui/error-message";

import { InputGroup } from "@/components/base/input/input-group";
import { AddressInput } from "@/components/form-input/address-input";
import { DateInput } from "@/components/form-input/date-input";
import { EmailInput } from "@/components/form-input/email-input";
import { FirstNameInput } from "@/components/form-input/first-name-input";
import { GenderInput } from "@/components/form-input/gender-input";
import { LastNameInput } from "@/components/form-input/last-name-input";
import { PasswordInput } from "@/components/form-input/password-input";
import { PhoneInput } from "@/components/form-input/phone-input";
import { PostalAddressInput } from "@/components/form-input/postal-address-input";
import { PostalCodeInput } from "@/components/form-input/postal-code-input";
import { UserContext } from "@/contexts/user-context";
import storageUtils from "@/utils/storage-utils";
import { useSession } from "next-auth/react";
import { type Dispatch, type SetStateAction, useContext, useEffect } from "react";
import { FormStateContext } from "../form-state-context";
import { type SessionContactInfo, SessionStorage } from "../types";
import { validateUserPassword } from "./validation";

interface ContactInfoFormProps {
  serverValidationErrors: ReadonlyArray<ErrorField>;
  validation: ReadonlyArray<Joi.ValidationErrorItem>;
  setValidation: Dispatch<SetStateAction<Joi.ValidationErrorItem[]>>;
  validateOnBlur: (fieldValue: string, fieldName: string) => Joi.ValidationErrorItem[] | undefined;
  validateEmail: (email: string) => Promise<void>;
}

export const ContactInfoForm = ({
  serverValidationErrors,
  validation,
  setValidation,
  validateOnBlur,
  validateEmail,
}: ContactInfoFormProps) => {
  const { formData, setFormData } = useContext(FormStateContext);
  const session = useSession();
  const { user } = useContext(UserContext);
  const { setItem } = storageUtils();

  useEffect(() => {
    if (session.status === "authenticated" && user?.email !== formData.contactInfo.email) {
      setFormData({
        contactInfo: {
          ...formData.contactInfo,
          email: user?.email ?? formData.contactInfo.email,
        },
      });
    }

    setItem<SessionContactInfo>(SessionStorage.ContactInfo, "SESSION", {
      contactInfo: formData.contactInfo,
      onBehalfOf: formData.onBehalfOf,
    });
  }, [formData.contactInfo, formData.onBehalfOf, setItem, session, user?.email, setFormData]);

  return (
    <>
      <InputGroup>
        <EmailInput
          externalValidationError={
            serverValidationErrors.find((error) => error.field === "email")?.errorCode === "EMAIL_ALREADY_EXISTS" ? (
              <ErrorMessage error="EMAIL_ALREADY_EXISTS" errorMessageFlavour={ErrorMessageFlavour.CREATE_USER} />
            ) : (
              validation.find((d) => d.context?.label === "E-postadresse")?.message
            )
          }
          onBlur={async () => {
            if (session?.status === "authenticated") {
              return;
            }
            const errors = validateOnBlur(formData.contactInfo.email, "email");
            if (!errors) {
              await validateEmail(formData.contactInfo.email);
            }
          }}
          onChange={(e) => {
            if (session?.status === "authenticated") {
              return;
            }
            setFormData({
              contactInfo: { ...formData.contactInfo, email: e.target.value },
            });
          }}
          value={formData.contactInfo.email}
          readOnly={session?.status === "authenticated"}
        />
      </InputGroup>
      <HorizontalSpacer />

      <InputGroup>
        <FirstNameInput
          externalValidationError={validation.find((d) => d.context?.label === "Fornavn")?.message}
          onBlur={() => validateOnBlur(formData.contactInfo.firstName, "firstName")}
          onChange={(e) =>
            setFormData({
              contactInfo: {
                ...formData.contactInfo,
                firstName: e.target.value,
              },
            })
          }
          value={formData.contactInfo.firstName}
        />

        <HorizontalSpacer />

        <LastNameInput
          externalValidationError={validation.find((d) => d.context?.label === "Etternavn")?.message}
          onBlur={() => validateOnBlur(formData.contactInfo.lastName, "lastName")}
          onChange={(e) =>
            setFormData({
              contactInfo: {
                ...formData.contactInfo,
                lastName: e.target.value,
              },
            })
          }
          value={formData.contactInfo.lastName}
        />

        <HorizontalSpacer />

        <PhoneInput
          externalValidationError={
            serverValidationErrors.find((error) => error.field === "mobilePhone.number")?.errorCode ===
            UserCreationErrorCode.PHONENUMBER_INVALID
              ? userCreationErrorsToString(UserCreationErrorCode.PHONENUMBER_INVALID)
              : validation?.find((error) => error.context?.label === "Telefonnummer")?.message
          }
          onBlur={() => validateOnBlur(formData.contactInfo.phone, "phone")}
          onChange={(e) =>
            setFormData({
              contactInfo: { ...formData.contactInfo, phone: e.target.value },
            })
          }
          value={formData.contactInfo.phone}
        />

        <HorizontalSpacer />

        <DateInput
          onBlur={() => validateOnBlur(formData.contactInfo.birthdate, "birthdate")}
          onChange={(date) =>
            setFormData({
              contactInfo: {
                ...formData.contactInfo,
                birthdate: dayjs(date).format("YYYY-MM-DD"),
              },
            })
          }
          showLabel={true}
          value={formData.contactInfo.birthdate}
        />
        {/* Siden DateInput er delt opp i 3 separate deler, viser vi ekstern feilmelding under feltet som helhet */}
        <InputErrorMessage
          errorMessage={undefined}
          externalErrorMessage={
            serverValidationErrors.find((error) => error.field === "birthDate") &&
            !validation.some((d) => d.context?.label === "Fødselsdato") // ikke vis hvis det er intern valideringsfeil
              ? serverValidationErrors.find((error) => error.field === "birthDate")?.errorCode
              : validation.find((d) => d.context?.label === "Fødselsdato")?.message
          }
        />
      </InputGroup>

      <HorizontalSpacer />

      <InputGroup>
        <AddressInput
          externalValidationError={validation.find((d) => d.context?.label === "Adresse")?.message}
          onBlur={() => validateOnBlur(formData.contactInfo.address, "address")}
          onChange={(e) =>
            setFormData({
              contactInfo: { ...formData.contactInfo, address: e.target.value },
            })
          }
          value={formData.contactInfo.address ?? ""}
        />
        <HorizontalSpacer />

        <PostalCodeInput
          externalValidationError={validation.find((d) => d.context?.label === "Postnummer")?.message}
          onBlur={() => validateOnBlur(formData.contactInfo.postalCode, "postalCode")}
          onChange={(e) =>
            setFormData({
              contactInfo: {
                ...formData.contactInfo,
                postalCode: e.target.value,
              },
            })
          }
          value={formData.contactInfo.postalCode}
        />
        <HorizontalSpacer />

        <PostalAddressInput
          externalValidationError={validation.find((d) => d.context?.label === "Poststed")?.message}
          onBlur={() => validateOnBlur(formData.contactInfo.postalAddress, "postalAddress")}
          onChange={(e) =>
            setFormData({
              contactInfo: {
                ...formData.contactInfo,
                postalAddress: e.target.value,
              },
            })
          }
          value={formData.contactInfo.postalAddress}
        />
      </InputGroup>
      <HorizontalSpacer size="medium" />

      <InputGroup>
        <HorizontalSpacer size={"x-small"} />
        <GenderInput
          externalErrorMessage={validation.find((d) => d.context?.label === "Kjønn")?.message}
          checkedAll={formData.contactInfo.gender === "ønsker ikke å oppgi"}
          checkedFemale={formData.contactInfo.gender === "kvinne"}
          checkedMale={formData.contactInfo.gender === "mann"}
          onChange={(e) =>
            setFormData({
              contactInfo: { ...formData.contactInfo, gender: e.target.value },
            })
          }
        />
      </InputGroup>

      <HorizontalSpacer size="medium" />

      {!formData.user.userId && (
        <InputGroup>
          <Heading type="h2">
            {formData.onBehalfOf.otherPayer ? "Lag et passord for betaler" : "Lag et passord"}
          </Heading>

          <HorizontalSpacer size={"small"} />

          <PasswordInput
            onChange={(e) =>
              setFormData({
                contactInfo: {
                  ...formData.contactInfo,
                  password: e.target.value,
                },
              })
            }
            onBlur={() => validateUserPassword(formData.contactInfo, setValidation, formData.user.userId, "Passord")}
            value={formData.contactInfo.password}
            externalValidationError={validation.find((d) => d.context?.label === "Passord")?.message}
          />
          <HorizontalSpacer />

          <PasswordInput
            id="password-confirmation"
            name="Gjenta passord"
            label="Gjenta passord"
            onBlur={() =>
              validateUserPassword(formData.contactInfo, setValidation, formData.user.userId, "Bekreft passord")
            }
            onChange={(e) =>
              setFormData({
                contactInfo: {
                  ...formData.contactInfo,
                  passwordConfirmation: e.target.value,
                },
              })
            }
            value={formData.contactInfo.passwordConfirmation}
            externalValidationError={validation.find((d) => d.context?.label === "Bekreft passord")?.message}
          />
        </InputGroup>
      )}
    </>
  );
};
